import { useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  IconHeart,
  IconLayoutGrid,
  IconListDetails,
} from "@tabler/icons-react";
import { Heading, Text } from "../../../../components/common/typography";
import { Button } from "../../../../components/common/button";
import { ViewToggle } from "../../../../components/common/view-toggle";
import { Pagination } from "../../../../components/common/pagination";
import { EmptyState } from "../../../../components/common/empty-state";
import { ShimmerLoader } from "../../../../components/common/shimmer";
import { ProductCard } from "../components/product-card";
import { ProductListRow } from "../components/product-list-row";
import {
  ProductsCompareBanner,
  type CompareTrayItem,
} from "../components/products-compare-banner";
import { useWishlistVariants } from "../../hooks/useWishlistVariants";
import { useWishlistStore } from "../../domain/stores/useWishlistStore";
import { AppRoutes } from "../../../../common/routes/app_routes";
import { getProductDetailsUrl } from "../../utils/get-product-details-url";
import type { ProductDto, ProductVariantDto } from "../../../../api";

type ViewMode = "grid" | "list";

const VIEW_MODE_KEY = "wishlist-view-mode";
const MAX_COMPARE = 3;
const PAGE_SIZE = 8;

/** Compare by product template — the id the compare page loads. */
function toCompareItem(variant: ProductVariantDto): CompareTrayItem {
  return {
    id: variant.productTemplateId ?? variant.id,
    name: variant.name,
    image: variant.image,
    description: variant.description,
  };
}

const viewOptions = [
  {
    value: "list" as const,
    icon: <IconListDetails size={20} />,
    "aria-label": "List view",
  },
  {
    value: "grid" as const,
    icon: <IconLayoutGrid size={20} />,
    "aria-label": "Grid view",
  },
] as const;

export function WishlistPage() {
  const navigate = useNavigate();
  const toggle = useWishlistStore((s) => s.toggle);
  const [viewMode, setViewMode] = useState<ViewMode>(
    () => (localStorage.getItem(VIEW_MODE_KEY) as ViewMode) ?? "grid",
  );
  const [compareMode, setCompareMode] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState<CompareTrayItem[]>([]);
  const [page, setPage] = useState(1);

  const { data, isLoading } = useWishlistVariants({
    page,
    pageSize: PAGE_SIZE,
  });

  const products = data?.data ?? [];
  const pageCount = data?.meta?.pageCount ?? 1;

  const selectedProductIds = useMemo(
    () => new Set(selectedProducts.map((p) => p.id)),
    [selectedProducts],
  );

  const handleProductToggle = useCallback((variant: ProductVariantDto) => {
    const item = toCompareItem(variant);
    setSelectedProducts((prev) => {
      const exists = prev.some((p) => p.id === item.id);
      if (exists) return prev.filter((p) => p.id !== item.id);
      if (prev.length >= MAX_COMPARE) return prev;
      return [...prev, item];
    });
  }, []);

  const handleAddToCompare = useCallback((product: ProductDto) => {
    setSelectedProducts((prev) =>
      prev.length >= MAX_COMPARE || prev.some((p) => p.id === product.id)
        ? prev
        : [...prev, product],
    );
  }, []);

  // The store reconciles the cached wishlist pages once the unlike lands.
  const handleUnlike = useCallback(
    (variantId: number) => void toggle(variantId),
    [toggle],
  );

  const handleCompareNow = () => {
    const ids = selectedProducts.map((p) => p.id).join(",");
    navigate(`${AppRoutes.PRODUCTS_COMPARE}?ids=${ids}`);
  };

  const handleClearCompare = () => {
    setSelectedProducts([]);
    setCompareMode(false);
  };

  const emptyState = (
    <div className="flex justify-center py-10">
      <EmptyState
        icon={IconHeart}
        size="sm"
        title="Your wishlist is empty"
        description="Save products you want to revisit and compare them here."
        primaryAction={{
          label: "Browse catalog",
          onClick: () => navigate(AppRoutes.PRODUCTS),
        }}
      />
    </div>
  );

  return (
    <div className="flex w-full flex-col gap-6 px-8 py-6 pb-24 max-md:gap-4 max-md:px-4">
      <div>
        <Heading variant="h2" tone="dark">
          Wishlist
        </Heading>
        <Text variant="bodyLg" tone="muted">
          Your saved products
        </Text>
      </div>

      <div className="flex items-center justify-between gap-4">
        <div className="flex shrink-0 items-center gap-3 ml-auto">
          <ViewToggle
            options={viewOptions}
            value={viewMode}
            onChange={(m) => {
              setViewMode(m);
              localStorage.setItem(VIEW_MODE_KEY, m);
            }}
            aria-label="Wishlist view mode"
          />
          <Button
            variant={compareMode ? "primary" : "secondary"}
            size="md"
            onClick={() => {
              setCompareMode((v) => !v);
              if (compareMode) setSelectedProducts([]);
            }}
          >
            Compare
          </Button>
        </div>
      </div>

      {isLoading ? (
        <ShimmerLoader message="Loading your wishlist…" />
      ) : products.length === 0 ? (
        emptyState
      ) : viewMode === "grid" ? (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {products.map((variant) => {
            const templateId = variant.productTemplateId ?? variant.id;
            return (
              <div
                key={variant.id}
                className="cursor-pointer"
                onClick={() =>
                  compareMode
                    ? handleProductToggle(variant)
                    : navigate(
                        getProductDetailsUrl(
                          templateId,
                          null,
                          AppRoutes.PRODUCT_DETAILS,
                        ),
                      )
                }
              >
                <ProductCard
                  product={variant as any}
                  isInWishlist
                  onToggleWishlist={() => handleUnlike(variant.id)}
                  onViewDetails={() =>
                    navigate(
                      getProductDetailsUrl(
                        templateId,
                        null,
                        AppRoutes.PRODUCT_DETAILS,
                      ),
                    )
                  }
                  selectable={compareMode}
                  selected={selectedProductIds.has(templateId)}
                  onToggleSelect={() => handleProductToggle(variant)}
                  compareMode={compareMode}
                />
              </div>
            );
          })}
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {products.map((variant) => {
            const templateId = variant.productTemplateId ?? variant.id;
            return (
              <ProductListRow
                key={variant.id}
                product={variant as any}
                isInWishlist
                onToggleWishlist={() => handleUnlike(variant.id)}
                onViewDetails={() =>
                  navigate(
                    AppRoutes.PRODUCT_DETAILS.replace(":id", String(templateId)),
                  )
                }
                onClick={() =>
                  compareMode
                    ? handleProductToggle(variant)
                    : navigate(
                        getProductDetailsUrl(
                          templateId,
                          null,
                          AppRoutes.PRODUCT_DETAILS,
                        ),
                      )
                }
                selectable={compareMode}
                selected={selectedProductIds.has(templateId)}
                onToggleSelect={() => handleProductToggle(variant)}
              />
            );
          })}
        </div>
      )}

      {pageCount > 1 && (
        <div className="flex justify-center">
          <Pagination
            page={page}
            pageCount={pageCount}
            onPageChange={setPage}
          />
        </div>
      )}

      <ProductsCompareBanner
        selectedProducts={selectedProducts}
        maxCompare={MAX_COMPARE}
        onRemove={(id) =>
          setSelectedProducts((prev) => prev.filter((p) => p.id !== id))
        }
        onAddProduct={handleAddToCompare}
        onCompare={handleCompareNow}
        onClear={handleClearCompare}
      />
    </div>
  );
}

export default WishlistPage;
